/** The design editor's Elements categories (see ElementForms for the shared pickers). */
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import type { FabricObject } from "fabric";
import type { AppDispatch } from "../../../store";
import {
  fetchStudioMusic,
  uploadStudioAsset,
} from "../../../features/studio-controller/StudioServices";
import type { MediaItem } from "../../../types/studioTypes";
import type { StudioMusicTrack, VideoStart } from "../../../types/videoType";
import { Icon } from "../Icon";
import { MediaPicker } from "../MediaPicker";
import {
  ChartSpec,
  TableSpec,
  makeChart,
  makeMockup,
  makeTable,
  placeOnPage,
  sampleChart,
  sampleTable,
} from "../elementKit";
import {
  AiElementForm,
  CategoryHead,
  ChartForm,
  ElementCategory,
  GraphicsGrid,
  MockupGrid,
  TableForm,
} from "./ElementForms";

/** What this file needs from the design editor. */
export interface DesignElementsApi {
  design: { width: number; height: number } | null;
  canvas: any; // fabric.Canvas
  selected: any;
  brand: { colors?: string[] };
  features: { aiElements?: boolean };
  add: (obj: any, select?: boolean) => void;
  insertImage: (item: { id: number | string; mediaUrl: string }) => Promise<unknown>;
  selectLayer: (o: any) => void;
  changed: () => void;
  refresh: () => void;
  notify: (text: string, kind?: "good" | "bad" | "info") => void;
  setPanel: (p: string) => void;
  openVideo?: (start: VideoStart) => void;
}

const HINT = "mt-2 text-[13px] leading-normal text-st-muted";
const H3 = "mb-[10px] mt-[18px] text-[13px] font-bold uppercase tracking-[0.06em] text-st-muted";
const BTN =
  "inline-flex min-h-[38px] w-full cursor-pointer items-center justify-center gap-2 rounded-10 border border-st-line bg-white px-3 py-2 text-sm font-semibold text-st-ink hover:border-[#9fb3ab] disabled:cursor-not-allowed disabled:opacity-50";

const errText = (e: unknown) => (typeof e === "string" ? e : e instanceof Error ? e.message : "Something went wrong");

/** Swaps the selected chart/table for a rebuilt one, keeping its place, size and angle. */
function replaceSelected(ed: DesignElementsApi, next: FabricObject) {
  const c = ed.canvas;
  const old = ed.selected as FabricObject | null;
  if (!c || !old) return;
  const k = (old.width * (old.scaleX || 1)) / next.width;
  next.set({ left: old.left, top: old.top, angle: old.angle, scaleX: k, scaleY: k, opacity: old.opacity });
  next.setCoords();
  const at = c.getObjects().indexOf(old);
  c.remove(old);
  c.insertAt(Math.max(0, at), next);
  ed.selectLayer(next);
  c.requestRenderAll();
  ed.changed();
  ed.refresh();
}

/** Captures one frame of a video as a PNG. */
function videoFrame(url: string, at = 0.5): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const v = document.createElement("video");
    v.crossOrigin = "anonymous";
    v.muted = true;
    v.preload = "auto";
    v.playsInline = true;
    v.onloadeddata = () => {
      v.currentTime = Math.min(at, Math.max(0, (v.duration || 1) - 0.1));
    };
    v.onseeked = () => {
      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      try {
        c.getContext("2d")?.drawImage(v, 0, 0);
        c.toBlob((b) => (b ? resolve(b) : reject(new Error("Couldn't read the video"))), "image/png");
      } catch {
        reject(new Error("This video can't be read in the browser. Try an MP4 file."));
      }
    };
    v.onerror = () => reject(new Error("This video can't be read in the browser. Try an MP4 file."));
    v.src = url;
  });
}

export function DesignElements({ ed, cat, onBack }: { ed: DesignElementsApi; cat: ElementCategory; onBack: () => void }) {
  const dispatch = useDispatch<AppDispatch>();
  const W = ed.design?.width ?? 1080;
  const H = ed.design?.height ?? 1080;
  const brandFill = ed.brand.colors?.[0] || "#0b7a5a";
  const [busy, setBusy] = useState(false);
  const [video, setVideo] = useState<MediaItem | null>(null);
  const [tracks, setTracks] = useState<StudioMusicTrack[] | null>(null);
  const [playing, setPlaying] = useState<string | number | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (cat !== "audio" || tracks) return;
    dispatch(fetchStudioMusic())
      .unwrap()
      .then((t) => setTracks(t as StudioMusicTrack[]))
      .catch(() => setTracks([]));
  }, [cat, tracks, dispatch]);
  useEffect(() => () => audio.current?.pause(), []);

  const selChart = ed.selected?.chart as ChartSpec | undefined;
  const selTable = ed.selected?.table as TableSpec | undefined;

  async function addVideoFrame(item: MediaItem) {
    setBusy(true);
    try {
      const blob = await videoFrame(item.mediaUrl);
      const name = `${(item.templateName || item.fileName || "video").replace(/\.[a-z0-9]+$/i, "")}_frame.png`;
      const file = new File([blob], name, { type: "image/png" });
      const saved = (await dispatch(uploadStudioAsset(file)).unwrap()) as MediaItem;
      await ed.insertImage(saved);
    } catch (e) {
      ed.notify(errText(e), "bad");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <CategoryHead cat={cat} onBack={onBack} />

      {cat === "graphics" && (
        <>
          <GraphicsGrid color={brandFill} onPick={(g) => ed.add(placeOnPage(g.make(brandFill), W, H, 0.35))} />
          <p className={HINT}>Ungroup a graphic to change its words. Shapes, lines, icons and stickers are on the main Elements list.</p>
        </>
      )}

      {cat === "photos" && (
        <>
          <MediaPicker type="IMAGE" compact onPick={(item) => ed.insertImage(item)} />
          <button type="button" className={`${BTN} mt-3`} onClick={() => ed.setPanel("photos")}>
            <Icon name="photo" size={16} /> Free stock photos and more
          </button>
        </>
      )}

      {cat === "videos" && (
        <>
          <p className={HINT}>
            Designs are pictures and PDFs, so a video can't play here. Use a frame of it as a photo, or edit the video in the
            video editor.
          </p>
          {video ? (
            <div className="mt-3 grid gap-2">
              <video src={video.mediaUrl} className="w-full rounded-xl bg-black" muted playsInline controls />
              <button type="button" className={BTN} disabled={busy} onClick={() => addVideoFrame(video)}>
                <Icon name="image" size={16} /> {busy ? "Adding…" : "Add a frame as a photo"}
              </button>
              {ed.openVideo && (
                <button type="button" className={BTN} onClick={() => ed.openVideo?.({ media: [video] })}>
                  <Icon name="video" size={16} /> Edit in the video editor
                </button>
              )}
              <button type="button" className="text-left text-sm font-semibold text-st-green-dark" onClick={() => setVideo(null)}>
                Choose another video
              </button>
            </div>
          ) : (
            <div className="mt-3">
              <MediaPicker type="VIDEO" compact onPick={setVideo} />
            </div>
          )}
        </>
      )}

      {cat === "audio" && (
        <>
          <audio ref={audio} onEnded={() => setPlaying(null)} />
          <p className={HINT}>
            Pictures and PDFs have no sound. Pick a track to start a video with it as background music.
          </p>
          {tracks === null && <p className={HINT}>Loading music…</p>}
          {tracks && tracks.length === 0 && <p className={HINT}>No music yet. Upload tracks in the video editor's Music panel.</p>}
          <ul className="m-0 mt-3 grid list-none gap-1 p-0">
            {(tracks || []).map((t) => (
              <li key={t.id} className="flex items-center gap-2 rounded-xl px-2 py-1.5 hover:bg-st-paper">
                <button
                  type="button"
                  className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full border-0 bg-st-ink text-white"
                  aria-label={playing === t.id ? `Stop ${t.title}` : `Listen to ${t.title}`}
                  onClick={() => {
                    const el = audio.current;
                    if (!el) return;
                    if (playing === t.id) {
                      el.pause();
                      setPlaying(null);
                      return;
                    }
                    el.src = t.url;
                    el.play().then(() => setPlaying(t.id)).catch(() => setPlaying(null));
                  }}
                >
                  <Icon name={playing === t.id ? "pause" : "play"} size={14} />
                </button>
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{t.title}</span>
                {ed.openVideo && (
                  <button
                    type="button"
                    className="flex-none rounded-lg border border-st-line bg-white px-2 py-1 text-xs font-semibold hover:border-st-green"
                    onClick={() => {
                      audio.current?.pause();
                      ed.openVideo?.({ tracks: [t] });
                    }}
                  >
                    Use in a video
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {cat === "ai" && <AiElementForm
          enabled={!!ed.features.aiElements}
          onMade={async (item) => {
            await ed.insertImage(item);
          }}
        />}

      {cat === "mockups" && (
        <>
          <MockupGrid onPick={(k) => ed.add(makeMockup(k, W, H))} />
          <p className={HINT}>Select the grey screen of a mockup, then pick a photo to put on it.</p>
        </>
      )}

      {cat === "charts" && (
        <>
          {selChart && <h3 className={H3}>Edit the selected chart</h3>}
          <ChartForm
            key={selChart ? `sel-${ed.selected?.id}` : "new"}
            initial={selChart ?? sampleChart("column", ed.brand.colors?.length ? ed.brand.colors : undefined)}
            brandColors={ed.brand.colors}
            submitLabel={selChart ? "Update chart" : "Add chart"}
            onSubmit={(spec) => {
              const g = makeChart(spec);
              if (selChart) replaceSelected(ed, g);
              else ed.add(placeOnPage(g, W, H, 0.75));
            }}
          />
        </>
      )}

      {cat === "tables" && (
        <>
          {selTable && <h3 className={H3}>Edit the selected table</h3>}
          <TableForm
            key={selTable ? `sel-${ed.selected?.id}` : "new"}
            initial={selTable ?? sampleTable(brandFill)}
            brandColors={ed.brand.colors}
            submitLabel={selTable ? "Update table" : "Add table"}
            onSubmit={(spec) => {
              const g = makeTable(spec);
              if (selTable) replaceSelected(ed, g);
              else ed.add(placeOnPage(g, W, H, 0.8));
            }}
          />
        </>
      )}
    </>
  );
}
